<template>
  <div
    :class="{
      'frisbuy-popup-image': true,
      'frisbuy-full-size': !popupSettings.image.cropToSquare,
    }"
  >
    <div class="frisbuy-image-area">
      <AdvertisingLabel
        v-if="post.adsMarker"
        class="frisbuy-advertising-marker"
        :backgroundColor="post.adsMarker.customization.backgroundColor"
        :color="post.adsMarker.customization.color"
        :offset="10"
        :arrowSettings="{ x: 16 }"
        placement="bottom-start"
      >
        <AdvertisingPopover
          :erid="post.adsMarker.info.erid"
          :advertiser="post.adsMarker.info.advertiser"
          :inn="post.adsMarker.info.inn"
          :note="post.adsMarker.info.note"
        />
      </AdvertisingLabel>

      <PositionBlockWrapper
        v-if="
          typeof popupImagePositionStyles !== 'undefined' &&
          Object.keys(popupImagePositionStyles).length > 0
        "
        :x="popupImagePositionStyles.x"
        :y="popupImagePositionStyles.y"
        :scale="popupImagePositionStyles.scale / 100"
      >
        <img
          v-if="imageSource.imageHighRes"
          :style="{ visibility: 'visible' }"
          ref="image"
          :src="getDomainSubstitute(imageSource.imageHighRes)"
          @load="loadImageHandler"
          @error="errorImageHandler"
        />
      </PositionBlockWrapper>
      <div
        v-else
        class="frisbuy-image-area__background-image"
        ref="photo"
        :style="{
          'background-image': backgroundImage,
        }"
      >
        <img
          v-if="imageSource.imageHighRes"
          ref="image"
          :src="getDomainSubstitute(imageSource.imageHighRes)"
          @load="loadImageHandler"
          @error="errorImageHandler"
        />
      </div>
    </div>

    <div
      v-if="
        media.linkList &&
        (popupSettings.showOneMarker || media.linkList.length > 1)
      "
      class="frisbuy-marker-area"
    >
      <template v-for="(link, index) in media.linkList">
        <a
          v-if="link.marker?.show"
          :key="link + index"
          :class="{
            'frisbuy-marker': true,
            'frisbuy-marker-hover': link.hover,
          }"
          :href="(!hideBuyButton(link) && link.link) ?? undefined"
          :target="
            Boolean(popupSettings.targetBlankLink) ? '_blank' : undefined
          "
          :style="markerStyle(link)"
          :title="link.sku.title"
          @click="linkClick(link, index)"
          @mouseenter="mouseOver(link, true)"
          @mouseleave="mouseOver(link, false)"
        >
          <span>{{ index + 1 }}</span>
        </a>
      </template>
    </div>
  </div>
</template>

<script>
// Modules
import Vue from "vue";
import {
  AdvertisingLabel,
  AdvertisingPopover,
} from "@frisbuy/frisbuy-ui-components";
// Components
import PositionBlockWrapper from "@/components/PositionBlockWrapper";
// Services
import { MediaCatcher } from "@/common/services/ErrorHandler/ErrorCatcher";
import { DomainSubstitute } from "@/common/services/DomainSubstitute/DomainSubstitute";
// Consts
import widgetTypes from "@/common/consts/widgetTypes";

export default {
  components: { PositionBlockWrapper, AdvertisingLabel, AdvertisingPopover },

  data() {
    return {
      markerAreaValue: null,
    };
  },

  props: {
    post: {},
    media: {},
    settings: {},
  },

  computed: {
    popupImagePositionStyles() {
      if (!this.post.media || !this.post.media[0].view) {
        return {};
      }

      const { preview, square, story } = this.post.media[0].view;

      if (!square) {
        return {};
      }

      return square;
    },

    popupSettings() {
      return this.settings.popup;
    },

    imageSource() {
      const { popupSettings } = this;

      return this.imageURL(this.media, true, popupSettings.image.highResImage);
    },

    backgroundImage() {
      const url = this.imageSource.imageLowRes ? `url(${this.imageSource.imageLowRes})` : "none";
      const background = this.media.background || 'none';
      return `${url}, ${background}`;
    },

    markerArea() {
      return (
        this.markerAreaValue || {
          width: 100,
          height: 100,
        }
      );
    },
  },

  methods: {
    markerStyle(link) {
      return {
        top: `${Math.max(5, Math.min(95, link.marker.y))}%`,
        left: `${Math.max(5, Math.min(95, link.marker.x))}%`,
        color: this.popupSettings.image.colorNumber,
      };
    },

    loadImageHandler() {
      const { post, media, isRetina } = this;
      const { photo, image } = this.$refs;

      if (!photo || !image?.src) return;

      photo.style.backgroundImage = `url(${image.src})`;

      if (isRetina) {
        media.imageHighResolutionUrl = image.src;
      } else {
        media.imageUrl = image.src;
      }
    },

    getDomainSubstitute(srcUrl) {
      return DomainSubstitute(srcUrl)
    },

    errorImageHandler(e) {
      MediaCatcher(e, 2, widgetTypes.GALLERY)
    },

    /*
      TODO: Rewrite the function.
      Function's parameters should not be changed
    */
    mouseOver(link, state) {
      link.hover = state;
    },

    linkClick(link, index) {
      const { post } = this;

      this.sendAnaliticEvent("ClickLink", {
        provPostId: this.post.instagramPost?.frisbuyId ?? this.post.providerPost.frisbuyId,
        fbProduct: post.frisbuyPost.id,
        igCode: post.instagramPost.shortcode,
        link: link.link,
        linklabel: link.title,
        offer: link.sku.offerId,
      });

      // this.sendGATrack('ClickLink_gallery', `fbProduct${ this.post.frisbuyPost.id }_igCode${ this.post.instagramPost.shortcode }_link${ link.id }_linklabel${ link.title }_offer${ link.sku.offerId }`);
      // this.sendYAMetrika('FrisbuyClickLink', this.$parent.postIndex, index, this.post.media.indexOf(this.media));
    },

    setNarkerArea(imageURL) {
      const img = new Image();
      const area = {
        width: 100,
        height: 100,
      };

      img.onload = () => {
        if (
          img.height / img.width == 1 ||
          this.popupSettings.image.cropToSquare
        ) {
          this.markerAreaValue = null;
          return;
        }

        const maxProperty = img.height > img.width ? "height" : "width";
        const minValue = Math.min(img.height, img.width);
        const maxValue = Math.max(img.height, img.width);
        const ratio = maxValue / minValue;

        area[maxProperty] = (minValue * 100) / maxValue;
        area[maxProperty === "height" ? "width" : "height"] =
          ((minValue / ratio) * 100) / maxValue;

        this.markerAreaValue = area;
      };

      img.src = imageURL.imageLowRes;
    },
  },

  created() {
    const { imageSource, setNarkerArea } = this;

    setNarkerArea(imageSource);
  },

  watch: {
    imageSource(value) {
      const { setNarkerArea } = this;

      setNarkerArea(value);
    },
  },
};
</script>

<style scoped lang="scss">
@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.frisbuy-popup-image {
  width: 100%;
  height: 100%;
  position: relative;

  overflow: hidden;

  //&.frisbuy-provider-vk {
  //  &:before {
  //    bottom: auto;
  //    top: 10px;
  //  }
  //}
  //&.frisbuy-provider-tiktok {
  //  &:before {
  //    bottom: auto;
  //    top: 10px;
  //  }
  //}
  //&.frisbuy-provider-instagram {
  //  &:before {
  //    bottom: auto;
  //    top: 10px;
  //  }
  //}
  //&.frisbuy-provider-frisbuy {
  //  &:before {
  //    bottom: auto;
  //    top: 10px;
  //  }
  //}

  &.frisbuy-full-size > div:first-child {
    .frisbuy-image-area__background-image {
      background-size: contain;
      display: block;
    }
  }

  .frisbuy-image-area {
    padding-bottom: 100%;

    img {
      display: block;
      position: relative;
      visibility: hidden;
    }

    .frisbuy-image-area__background-image {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-size: cover;
      background-repeat: no-repeat;
      background-position: 50% 50%;
    }
  }

  .frisbuy-marker-area {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }
}
</style>

<style lang="scss">
.frisbuy-popup-image {
  .frisbuy-image-area {
    .frisbuy-advertising-marker {
      position: relative;

      width: fit-content;

      margin: 18px 0 0 16px;

      z-index: 3;

      .frisbuy-popover__tooltip {
        // 32px - double left margin of .frisbuy-advertising-marker
        width: calc(100vw - 32px);
        max-width: 340px;
      }
    }
  }
}
</style>
